import { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowLeft, Upload, Image as ImageIcon, Trash2, Check } from "lucide-react";
import api from "../lib/api";
import { resolveImg } from "../lib/img";
import { useAuth } from "../lib/auth";

const ENTITY_LABELS = {
    city: "Villes",
    place: "Adresses",
    experience: "Expériences",
    article: "Articles",
    walk: "Balades",
    parcours: "Parcours",
};

function GalleryThumb({ url, onRemove, canRemove }) {
    return (
        <div className="relative group aspect-square overflow-hidden bg-cp-craie border border-cp-border">
            <img src={resolveImg(url)} alt="" className="w-full h-full object-cover cp-photo-real" loading="lazy" />
            {canRemove && (
                <button
                    onClick={onRemove}
                    data-testid={`gallery-remove-${url.split("/").pop()}`}
                    className="absolute top-2 right-2 bg-cp-noir/80 text-cp-ivoire p-1.5 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-800"
                    title="Retirer de la galerie"
                >
                    <Trash2 size={14} strokeWidth={1.5} />
                </button>
            )}
        </div>
    );
}

function EntityRow({ entity, type, onUpload, onRemoveGallery, busySlug }) {
    const heroInputRef = useRef(null);
    const galleryInputRef = useRef(null);
    const busy = busySlug === entity.slug;

    return (
        <article
            className="border border-cp-border bg-cp-ivoire p-5 md:p-6 grid grid-cols-1 md:grid-cols-[180px_1fr] gap-5"
            data-testid={`entity-row-${type}-${entity.slug}`}
        >
            <div>
                {entity.hero_image ? (
                    <div className="aspect-[4/5] overflow-hidden bg-cp-craie">
                        <img src={resolveImg(entity.hero_image)} alt="" className="w-full h-full object-cover cp-photo-real" />
                    </div>
                ) : (
                    <div className="aspect-[4/5] grid place-items-center bg-cp-craie text-cp-gris">
                        <ImageIcon size={28} strokeWidth={1} />
                    </div>
                )}
            </div>

            <div className="flex flex-col">
                <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                        <p className="cp-kicker text-[9px] text-cp-foret">{type}</p>
                        <h3 className="cp-serif text-xl md:text-2xl text-cp-noir leading-tight">{entity.label}</h3>
                        <p className="cp-tiny text-cp-gris mt-1">{entity.slug}{entity.city_slug ? ` · ${entity.city_slug}` : ""}</p>
                    </div>
                    {busy && <span className="cp-tiny text-cp-foret animate-pulse">Téléversement…</span>}
                </div>

                <div className="flex flex-wrap gap-2 mt-3">
                    <input
                        ref={heroInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                        data-testid={`hero-input-${type}-${entity.slug}`}
                        onChange={(e) => e.target.files?.[0] && onUpload(type, entity.slug, e.target.files[0], "hero", e.target)}
                    />
                    <button
                        onClick={() => heroInputRef.current?.click()}
                        disabled={busy}
                        data-testid={`hero-btn-${type}-${entity.slug}`}
                        className="cp-kicker text-[10px] px-3 py-2 border border-cp-foret text-cp-foret hover:bg-cp-foret hover:text-cp-ivoire transition-colors flex items-center gap-1.5 disabled:opacity-50"
                    >
                        <Upload size={12} strokeWidth={1.5} /> Remplacer héros
                    </button>

                    <input
                        ref={galleryInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                        data-testid={`gallery-input-${type}-${entity.slug}`}
                        onChange={(e) => e.target.files?.[0] && onUpload(type, entity.slug, e.target.files[0], "gallery", e.target)}
                    />
                    <button
                        onClick={() => galleryInputRef.current?.click()}
                        disabled={busy}
                        data-testid={`gallery-btn-${type}-${entity.slug}`}
                        className="cp-kicker text-[10px] px-3 py-2 border border-cp-border text-cp-noir hover:border-cp-foret transition-colors flex items-center gap-1.5 disabled:opacity-50"
                    >
                        <ImageIcon size={12} strokeWidth={1.5} /> Ajouter à la galerie
                    </button>
                </div>

                {entity.images?.length > 0 && (
                    <div className="mt-5">
                        <p className="cp-tiny text-cp-gris mb-2">Galerie ({entity.images.length})</p>
                        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2">
                            {entity.images.map((url) => (
                                <GalleryThumb
                                    key={url}
                                    url={url}
                                    canRemove={!busy}
                                    onRemove={() => onRemoveGallery(type, entity.slug, url)}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </article>
    );
}

export default function AdminPhotos() {
    const { email, loading: authLoading } = useAuth();
    const [data, setData] = useState(null);
    const [activeTab, setActiveTab] = useState("city");
    const [search, setSearch] = useState("");
    const [busySlug, setBusySlug] = useState("");
    const [toast, setToast] = useState("");

    const load = () => {
        api.get("/admin/photos/entities").then((r) => setData(r.data)).catch(() => setData(null));
    };

    useEffect(() => {
        if (email) load();
    }, [email]);

    const showToast = (msg) => {
        setToast(msg);
        setTimeout(() => setToast(""), 2500);
    };

    const onUpload = async (type, slug, file, mode, inputEl) => {
        setBusySlug(slug);
        const form = new FormData();
        form.append("file", file);
        form.append("entity_type", type);
        form.append("slug", slug);
        form.append("mode", mode);
        try {
            await api.post("/admin/photos/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
            showToast(mode === "hero" ? `Héros remplacé pour ${slug}` : `Photo ajoutée à la galerie de ${slug}`);
            load();
        } catch (err) {
            showToast(err?.response?.data?.detail || "Erreur upload");
        } finally {
            setBusySlug("");
            if (inputEl) inputEl.value = "";
        }
    };

    const onRemoveGallery = async (type, slug, url) => {
        if (!window.confirm("Retirer cette photo de la galerie ?")) return;
        setBusySlug(slug);
        try {
            await api.post("/admin/photos/gallery/remove", { entity_type: type, slug, url });
            showToast("Photo retirée");
            load();
        } catch (err) {
            showToast(err?.response?.data?.detail || "Erreur suppression");
        } finally {
            setBusySlug("");
        }
    };

    const filtered = useMemo(() => {
        if (!data) return [];
        const rows = data[activeTab] || [];
        const q = search.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((r) =>
            [r.label, r.slug, r.city_slug].filter(Boolean).some((v) => v.toLowerCase().includes(q))
        );
    }, [data, activeTab, search]);

    if (authLoading) return <div className="pt-40 text-center text-cp-gris">Chargement…</div>;
    if (!email) return <Navigate to="/admin/login" replace />;

    return (
        <div className="min-h-screen bg-cp-craie" data-testid="admin-photos">
            <header className="bg-cp-ivoire border-b border-cp-border">
                <div className="max-w-[1600px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
                    <div className="flex items-center gap-6">
                        <Link to="/admin" className="cp-kicker text-[10px] text-cp-noir hover:text-cp-foret flex items-center gap-2" data-testid="back-to-admin">
                            <ArrowLeft size={14} strokeWidth={1.5} /> Tableau de bord
                        </Link>
                        <span className="cp-serif text-xl text-cp-noir">Photos</span>
                    </div>
                    <p className="cp-tiny text-cp-gris">{email}</p>
                </div>
            </header>

            <main className="max-w-[1600px] mx-auto px-6 md:px-12 py-10">
                <div className="mb-8">
                    <h1 className="cp-serif text-4xl md:text-5xl leading-tight text-cp-noir">Gestion des photos</h1>
                    <p className="cp-serif italic text-cp-gris mt-3 max-w-2xl">
                        Remplacez les images oniriques par de vrais clichés officiels. JPEG, PNG ou WebP — 8 Mo max par photo.
                    </p>
                </div>

                <div className="flex flex-wrap gap-2 mb-6" data-testid="admin-photos-tabs">
                    {Object.entries(ENTITY_LABELS).map(([k, l]) => {
                        const count = data?.[k]?.length || 0;
                        const active = activeTab === k;
                        return (
                            <button
                                key={k}
                                onClick={() => setActiveTab(k)}
                                data-testid={`tab-${k}`}
                                className={`cp-kicker text-[10px] px-4 py-2 border transition-all ${active ? "border-cp-foret bg-cp-foret text-cp-ivoire" : "border-cp-border text-cp-noir hover:border-cp-foret"}`}
                            >
                                {l} <span className="ml-1 opacity-70">({count})</span>
                            </button>
                        );
                    })}
                </div>

                <div className="mb-6">
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Filtrer par slug, nom ou ville…"
                        data-testid="admin-photos-search"
                        className="w-full md:max-w-md bg-cp-ivoire border border-cp-border px-4 py-3 text-cp-noir placeholder:text-cp-gris focus:outline-none focus:border-cp-foret transition-colors"
                    />
                </div>

                {!data ? (
                    <p className="text-cp-gris italic">Chargement…</p>
                ) : filtered.length === 0 ? (
                    <p className="text-cp-gris italic">Aucun élément.</p>
                ) : (
                    <div className="grid grid-cols-1 gap-5" data-testid="admin-photos-list">
                        {filtered.map((entity) => (
                            <EntityRow
                                key={`${activeTab}-${entity.slug}`}
                                entity={entity}
                                type={activeTab}
                                onUpload={onUpload}
                                onRemoveGallery={onRemoveGallery}
                                busySlug={busySlug}
                            />
                        ))}
                    </div>
                )}
            </main>

            {toast && (
                <div
                    data-testid="admin-photos-toast"
                    className="fixed bottom-6 right-6 bg-cp-foret text-cp-ivoire px-5 py-3 cp-serif text-sm flex items-center gap-2 shadow-lg z-50"
                >
                    <Check size={14} strokeWidth={1.5} /> {toast}
                </div>
            )}
        </div>
    );
}
